import { SEOHead } from '@/features/shared/components/SEOHeader/SEOHead'
import { type MainEntity, SEO_PAGE_TYPE } from '@/features/shared/components/SEOHeader/types'
import { type CMSLayout } from '@/features/cms/components/CMS/types'
import { getRetailerUrl, routes } from '@shared/constants/routes'
import { getProductPageUrl } from '@/features/shared/utils/getProductPageUrl'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'

export const SEOWrapper = ({
  cmsContent,
  children,
}: {
  cmsContent?: CMSLayout
  children: React.ReactNode
}) => {
  const pageTitle = 'Shop Stores Near You for Same-Day Delivery | Shipt'
  const pageDescription =
    'Browse real-time inventory from nearby stores like Target, Meijer & Kroger. Compare prices & add items to your cart for fast, same-day delivery with Shipt.'
  const canonicalUrl = `https://www.shipt.com${routes.GLOBAL_HOMEPAGE.url}`
  let pageTypeSchema
  const entities: MainEntity[] = []

  const storeCards =
    cmsContent?.content_type_id === 'global_home_page_layout' &&
    cmsContent.data?.body_section_a?.find((item) => item.content_type_id === 'stores_carousel')
      ?.data?.store_cards

  if (storeCards) {
    entities.push({
      '@type': 'ItemList',
      itemListElement: storeCards.map(({ data: store }) => ({
        '@type': SEO_PAGE_TYPE.LOCAL_BUSINESS,
        name: store.name,
        url: `https://www.shipt.com${getRetailerUrl(store.name)}`,
        logo: store.logo.src,
      })),
    })
  }

  const products =
    cmsContent?.content_type_id === 'global_home_page_layout'
      ? [
          ...(cmsContent.data?.body_section_a?.find(
            (item) => item.content_type_id === 'product_shelf',
          )?.data.items ?? []),
          ...(cmsContent.data?.body_section_b?.find(
            (item) => item.content_type_id === 'product_shelf',
          )?.data.items ?? []),
        ]
      : []

  if (products.length > 0) {
    entities.push({
      '@type': 'ItemList',
      itemListElement: products
        // We only want to include products that we can provide a url to
        .filter((product) => Boolean(product.url_slug && product.universal_id))
        .map((product) => ({
          '@type': SEO_PAGE_TYPE.PRODUCT,
          name: product.display_name || 'Product',
          image: product.image?.url,
          url: `https://www.shipt.com${getProductPageUrl({
            universal_id: product.universal_id ?? '',
            ungated: true,
          })}`,
          brand: {
            '@type': 'Brand',
            name: product.brand_name || 'Shipt',
          },
          offers: product.price
            ? {
                '@type': 'Offer',
                priceCurrency: 'USD',
                price: product.price.toFixed(2),
                availability: 'https://schema.org/InStock',
                itemCondition: 'https://schema.org/NewCondition',
                url: `https://www.shipt.com${getProductPageUrl({
                  universal_id: product.universal_id ?? '',
                  ungated: true,
                })}`,
                priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1))
                  .toISOString()
                  .split('T')[0],
                seller: {
                  '@type': 'Organization',
                  name: 'Shipt',
                },
              }
            : undefined,
        })),
    })
  }

  const categories =
    cmsContent?.content_type_id === 'global_home_page_layout' &&
    cmsContent.data?.body_section_a?.find((item) => item.content_type_id === 'standard_nav')?.data
      .nav_links

  if (categories) {
    entities.push({
      '@type': 'ItemList',
      itemListElement: categories.map(({ data: category }) => ({
        '@type': SEO_PAGE_TYPE.COLLECTION_PAGE,
        name: category.heading,
        url:
          category.link.type === 'relative'
            ? `https://www.shipt.com${category.link.url}`
            : category.link.url,
        image: category.image.src,
      })),
    })
  }

  if (entities.length > 0) {
    pageTypeSchema = {
      '@context': 'https://schema.org',
      '@type': [SEO_PAGE_TYPE.WEB_PAGE, SEO_PAGE_TYPE.COLLECTION_PAGE],
      name: pageTitle,
      description: pageDescription,
      url: canonicalUrl,
      image: {
        url: objectStorageUrl('get-started-hero.png'),
        width: 872,
        height: 1072,
      },
      mainEntity: entities,
    }
  }

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        pageTypeSchema={pageTypeSchema}
      />
      {children}
    </>
  )
}
